import {
  transport,
  useTransportMutation,
  useTransportQueryUtils,
} from "@/transport"
import { useQueryClient } from "@tanstack/react-query"

import { invalidateHomeQueries } from "@/lib/home-query-cache"
import type { CreatePersonaRequest } from "@/types_api/backend/transport/create_persona_transport_request"
import type { UpdatePersonaRequest } from "@/types_api/backend/transport/update_persona_transport_request"

import { mapTransportPersonaToPersona } from "./usePersonasTransport"

type UpdatePersonaInput = Omit<UpdatePersonaRequest, "persona_id"> & {
  id: string
}

export const usePersonaPersistenceTransport = () => {
  const queryClient = useQueryClient()
  const transportQueryUtils = useTransportQueryUtils()

  const invalidatePersonasList = () =>
    transportQueryUtils.invalidate(transport.resources.personas)

  const createMutation = useTransportMutation(
    transport.operations.createPersona,
    {
      onSuccess: () => {
        void invalidatePersonasList()
        void invalidateHomeQueries(queryClient)
      },
    },
  )

  const updateMutation = useTransportMutation(
    transport.operations.updatePersona,
    {
      onSuccess: (_data, variables) => {
        void invalidatePersonasList()
        if (variables) {
          void transportQueryUtils.invalidate(transport.operations.getPersona, {
            persona_id: variables.persona_id,
          })
        }
      },
    },
  )

  const deleteMutation = useTransportMutation(
    transport.operations.deletePersona,
    {
      onSuccess: () => {
        void invalidatePersonasList()
        void invalidateHomeQueries(queryClient)
      },
    },
  )

  const deleteBatchMutation = useTransportMutation("deletePersonas", {
    onMutate: () => transportQueryUtils,
    onSettled: (_data, _error, _variables, queryUtils) => {
      if (_error || !queryUtils) {
        return
      }
      void queryUtils.invalidateResource("personas")
      void queryUtils.invalidateOperation("listRecentlyDeletedPersonas")
      void invalidateHomeQueries(queryClient)
    },
  })

  const duplicateMutation = useTransportMutation(
    transport.operations.duplicatePersona,
    {
      onSuccess: () => {
        void invalidatePersonasList()
        void invalidateHomeQueries(queryClient)
      },
    },
  )

  const revertVersionMutation = useTransportMutation(
    transport.operations.revertPersonaVersion,
    {
      onSuccess: (_data, variables) => {
        void invalidatePersonasList()
        if (variables) {
          void transportQueryUtils.invalidate(transport.operations.getPersona, {
            persona_id: variables.persona_id,
          })
        }
      },
    },
  )

  return {
    createPersona: async (request: CreatePersonaRequest) =>
      mapTransportPersonaToPersona(
        (await createMutation.mutateAsync(request)).persona,
      ),
    updatePersona: async ({ id, ...request }: UpdatePersonaInput) =>
      mapTransportPersonaToPersona(
        (
          await updateMutation.mutateAsync({
            persona_id: id,
            ...request,
          })
        ).persona,
      ),
    deletePersona: deleteMutation.mutateAsync,
    deletePersonas: deleteBatchMutation.mutateAsync,
    duplicatePersona: async (persona_id: string) =>
      mapTransportPersonaToPersona(
        (await duplicateMutation.mutateAsync({ persona_id })).persona,
      ),
    revertPersonaVersion: async ({
      personaId,
      versionId,
    }: {
      personaId: string
      versionId: string
    }) =>
      mapTransportPersonaToPersona(
        (
          await revertVersionMutation.mutateAsync({
            persona_id: personaId,
            version_id: versionId,
          })
        ).persona,
      ),
    isSaving: createMutation.isPending || updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isDuplicating: duplicateMutation.isPending,
    isReverting: revertVersionMutation.isPending,
  }
}
